/**
 * Where the demo lives, and what the visitor came for.
 *
 * This is the one file on the marketing site that knows another origin exists.
 * Everything else here is a picture of the product; the overlay is the product,
 * framed.
 *
 * **The origin is the Studio host, not the API host, and that is deliberate.**
 * The stage drives the clone over a bridge that accepts commands only from its
 * own origin, and `studio.persivo.co` proxies `/_stage/` and every clone to the
 * backend under one browser origin. Pointing this at the API service directly
 * would still load — and then silently fail to move the page.
 */
const FALLBACK = import.meta.env.DEV ? 'http://localhost:7332' : 'https://studio.persivo.co';

export const DEMO_ORIGIN: string = (import.meta.env.VITE_DEMO_ORIGIN || FALLBACK).replace(/\/$/, '');

/**
 * Which demo, and why this names a product rather than a recording.
 *
 * A slug pins a *take*. Re-recording Persivo produces a new package — often a
 * new directory — and a pinned slug would leave the public demo on the previous
 * one until somebody remembered that a static site has to be rebuilt and
 * redeployed before the marketing page catches up. That is a silent staleness,
 * which is the worst kind.
 *
 * So the site asks for the product and the backend resolves the newest packed
 * take at request time, as a redirect on the frame's own navigation — no extra
 * round trip, and no rebuild when a new recording lands. `VITE_DEMO_FLOW` still
 * pins one if a specific take ever needs to be the public face.
 */
export const DEMO_SITE: string = import.meta.env.VITE_DEMO_SITE || 'localhost';
export const DEMO_FLOW: string = import.meta.env.VITE_DEMO_FLOW || '';

/**
 * The chip a visitor pressed, as something a person would actually say.
 *
 * The page promises the tour takes a different route depending on what you came
 * for, and this is where that promise is kept. It is carried in as `?ask=` and
 * spoken as the buyer's opening line — so it lands in the transcript as their
 * words, which it is: they chose it. Nothing is added to the agent's brief
 * behind their back.
 */
const OPENERS: Record<string, string> = {
  /*
    The first chip is the product's own claim, said back to it.

    Every other opener asks the guide to SHOW something, which the demo has
    always been able to do. This one asks it to CHANGE something — and the guide
    will, on this page, with whatever name the visitor gave it. It is first
    because it is the only line here that a recorded walkthrough could not
    answer, and the page should lead with the part that is not a video.
  */
  'Configure it for us': 'Before the tour — show me what this looks like configured for us. We would call the guide Nova.',
  'Evaluating it': 'I am evaluating this. Show me what it actually does.',
  'Security review': 'I am here for a security review — what leaves our network, and what gets stored?',
  'Admin setup': 'Show me what standing this up takes on our side.',
  'Pricing': 'Walk me through what this costs, and what changes the number.',
  'Integrations': 'What does this connect to, and how?',
};

export function demoUrl(purpose?: string, typed?: string): string {
  const url = new URL(`${DEMO_ORIGIN}/_stage/`);
  if (new URLSearchParams(location.search).get('recording') === 'persivo') {
    url.searchParams.set('recording', 'persivo');
    url.searchParams.set('embed', '1');
    return url.toString();
  }
  if (DEMO_FLOW) url.searchParams.set('flow', DEMO_FLOW);
  else url.searchParams.set('site', DEMO_SITE);
  url.searchParams.set('embed', '1');
  // What somebody typed into the Ask bar is their own first line, word for word (the stage clamps it at 240
  // characters too); a chip carries the opener written for it.
  const ask = typed?.trim().slice(0, 240) || (purpose ? OPENERS[purpose] : '');
  if (ask) url.searchParams.set('ask', ask);
  return url.toString();
}

/**
 * Shaving the handshake off the open.
 *
 * DNS, TCP and TLS to a cold cross-origin host is a few hundred milliseconds
 * that happen *after* the click if nothing is done, and they are the part of the
 * wait that buys nothing. Doing it when the visitor reaches for the button costs
 * one connection and is idempotent — the browser ignores a second identical
 * hint.
 */
let warmed = false;
export function warmDemo(): void {
  if (warmed || typeof document === 'undefined') return;
  warmed = true;
  for (const rel of ['preconnect', 'dns-prefetch']) {
    const link = document.createElement('link');
    link.rel = rel;
    link.href = DEMO_ORIGIN;
    if (rel === 'preconnect') link.crossOrigin = '';
    document.head.appendChild(link);
  }
}
